import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../utils/apiClient";
import CustomerCaisseModal from "./CustomerCaisseModal";

export const reserveLineKey = (item) => String(item.order_item_id || item.orderItemId || item.client_line_id || item.id || "");
const money = (minor) => `${(Number(minor || 0) / 100).toLocaleString("fr-FR")} F`;

export default function ReserveCheckoutPanel({ items = [], value, onChange, disabled = false, invoiceAmount = 0 }) {
  const [settings, setSettings] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const searchSequence = useRef(0);
  useEffect(() => {
    let current = true;
    apiFetch("/api/reserves/settings", { includeBearer: true }).then(async (response) => {
      if (response.ok && current) setSettings(await response.json());
    }).catch(() => {});
    return () => { current = false; };
  }, []);
  if (!settings?.ready) return null;
  const mode = value?.mode || "none";
  const isActive = Boolean(value);
  const find = async () => {
    const sequence = ++searchSequence.current;
    setBusy(true); setError("");
    try {
      const query = new URLSearchParams({ search, status: "all", pageSize: "25" });
      if (contact?.id) query.set("customerId", String(contact.id));
      const response = await apiFetch(`/api/reserves?${query}`, { includeBearer: true });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Recherche impossible");
      if (sequence === searchSequence.current) {
        setResults(payload.items.filter((item) => item.balance_minor > 0));
        setHasSearched(true);
      }
    } catch (failure) { if (sequence === searchSequence.current) setError(failure.message); }
    finally { if (sequence === searchSequence.current) setBusy(false); }
  };
  const changeMode = (next) => {
    setResults([]); setHasSearched(false); setError("");
    onChange({ mode: next, selections: [], customerId: contact?.id || null });
  };
  const toggle = () => {
    setResults([]); setHasSearched(false); setError("");
    onChange(isActive ? null : { mode: "choose", selections: [], customerId: contact?.id || null });
  };
  const updateIssueQuantity = (lineKey, quantity, maximum) => {
    const normalizedMaximum = Math.max(0, Math.floor(Number(maximum) || 0));
    const normalizedQuantity = Math.min(
      normalizedMaximum,
      Math.max(0, Math.floor(Number(quantity) || 0)),
    );
    onChange({
      ...value,
      selections: [
        ...(value.selections || []).filter((line) => line.lineKey !== lineKey),
        ...(normalizedQuantity > 0
          ? [{ lineKey, quantity: normalizedQuantity }]
          : []),
      ],
    });
  };
  return <section className="my-3 text-white" aria-label="Consignation">
    <div className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${isActive ? "border-amber-300/40 bg-amber-300/5" : "border-white/15 bg-white/[0.03]"}`}>
      <span className="font-semibold">Consignation</span>
      <button type="button" role="switch" aria-label="Activer la consignation pour ce paiement" aria-checked={isActive}
        disabled={disabled || settings.consultationOnly} onClick={toggle}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${isActive ? "bg-amber-300" : "bg-slate-600"}`}>
        <span aria-hidden="true" className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${isActive ? "left-6" : "left-1"}`} />
      </button>
    </div>
    {settings.consultationOnly && <p className="mt-2 text-sm text-amber-200">Utilisation sur le serveur local de l’établissement uniquement.</p>}
    {isActive && <div className="mt-3 rounded-xl border border-amber-300/30 bg-amber-300/5 p-4">
      <label className="block font-semibold">Que souhaitez-vous faire ?
        <select aria-label="Opération de consignation" value={["issue", "redeem"].includes(mode) ? mode : ""} disabled={disabled || settings.consultationOnly} onChange={(event) => changeMode(event.target.value)} className="mt-2 block w-full rounded-lg bg-slate-900 p-3 text-white">
          <option value="" disabled>Choisir une opération</option>
          {settings.enabled && <option value="issue">Mettre des articles en réserve</option>}
          <option value="redeem">Utiliser un montant déjà payé</option>
        </select>
    </label>
    {["issue", "redeem"].includes(mode) && <>
      <button type="button" disabled={disabled} onClick={() => setShowContacts(true)} className="mt-3 rounded-lg border border-white/20 p-2">{contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : "Associer un contact (facultatif)"}</button>
      {contact && <button type="button" disabled={disabled} className="ml-2 p-2" onClick={() => { setContact(null); onChange({ ...value, customerId: null }); }}>Retirer le contact</button>}
      {mode === "issue" ? <div className="mt-3 space-y-2">
        {items.map((item) => {
          const key = reserveLineKey(item);
          const selection = value.selections?.find((line) => line.lineKey === key);
          const maximum = Math.max(0, Math.floor(Number(item.quantity) || 0));
          const quantity = Math.min(maximum, Math.max(0, Number(selection?.quantity) || 0));
          return <div key={key} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 p-2">
            <span>{item.product_name || item.name || "Article"}</span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                aria-label={`Retirer une unité de ${item.product_name || item.name || "Article"}`}
                disabled={disabled || quantity <= 0}
                onClick={() => updateIssueQuantity(key, quantity - 1, maximum)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-xl font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-35"
              >
                −
              </button>
              <input
                aria-label={`Quantité à réserver : ${item.product_name || item.name || "Article"}`}
                type="number"
                inputMode="numeric"
                min="0"
                max={maximum}
                step="1"
                placeholder="0"
                value={quantity > 0 ? quantity : ""}
                disabled={disabled}
                className="h-10 w-20 rounded-lg bg-slate-900 px-2 text-center text-lg font-semibold text-white placeholder:text-white/35"
                onChange={(event) => {
                  if (event.target.value === "") {
                    updateIssueQuantity(key, 0, maximum);
                    return;
                  }
                  updateIssueQuantity(key, event.target.value, maximum);
                }}
              />
              <button
                type="button"
                aria-label={`Ajouter une unité de ${item.product_name || item.name || "Article"}`}
                disabled={disabled || quantity >= maximum}
                onClick={() => updateIssueQuantity(key, quantity + 1, maximum)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-300 text-xl font-semibold text-slate-950 transition hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-35"
              >
                +
              </button>
            </span>
          </div>;
        })}
      </div> : <div className="mt-3 space-y-3">
        <label className="block">Numéro de reçu<input value={search} onChange={(event) => { setSearch(event.target.value); setResults([]); setHasSearched(false); setError(""); }} className="mt-1 block w-full rounded-lg bg-slate-900 p-3 text-white" /></label>
        <button type="button" onClick={find} disabled={busy || disabled} className="rounded-lg bg-amber-300 px-4 py-2 text-slate-950">{busy ? "Recherche…" : "Rechercher"}</button>
        {!busy && !error && hasSearched && results.length === 0 && <p role="status">Aucune réserve disponible trouvée.</p>}
        {results.map((result) => <button type="button" key={result.id} disabled={disabled} onClick={() => onChange({ mode: "redeem", reserveId: result.id, amount: Math.min(invoiceAmount, result.balance_minor / 100) })} className={`block w-full rounded-lg border p-3 text-left ${value.reserveId === result.id ? "border-amber-300" : "border-white/20"}`}>
          {result.public_reference || result.origin_reference} · Disponible : {money(result.balance_minor)}
        </button>)}
        {value.reserveId && <label className="block">Montant à utiliser (F)<input type="number" min="0" max={invoiceAmount} step="0.01" value={value.amount ?? ""} disabled={disabled} onChange={(event) => onChange({ ...value, amount: Number(event.target.value) })} className="mt-1 block w-full rounded-lg bg-slate-900 p-3 text-white" /></label>}
      </div>}
    </>}
    {error && <p role="alert" className="mt-3 text-red-300">{error}</p>}
    </div>}
    <CustomerCaisseModal isOpen={showContacts} selectionMode selectionContext="consignation" onClose={() => setShowContacts(false)} onSelectCustomer={(customer) => { setContact(customer); setShowContacts(false); if (value) onChange({ ...value, customerId: customer.id }); }} />
  </section>;
}
